<template>
  <Modal :value="showRatingModal" small @input="closeRatingModal">
    <div class="rating">
      <div>
        <h1 class="basic-blue-title">
          {{ isGlobalRating ? $t('message.GLOBAL_RATING_TITLE') : $t('message.RATING_TITLE') }}
        </h1>
        <p class="rating__subtitle__question">
          {{ isGlobalRating ? $t('message.GLOBAL_RATING_SUBTITLE') : modalSubtitle }}
        </p>
      </div>
      <div class="rating__wrapper">
        <div class="rating__wrapper__stars">
          <Icon
            v-for="(star, key) in 5"
            :key="key"
            class="rating__wrapper__stars__item"
            :style="starStatus(key)"
            @mouseenter.native="starsHovered = key"
            @mouseleave.native="starsHovered = null"
            @click.native="onStarClicked(key)"
          >
            {{ starMedium }}
          </Icon>
        </div>
        <p
          class="mt10em option-button rating__wrapper__stars__item__none"
          :style="zeroStarStatus"
          @click="onStarClicked('NONE')"
        >
          {{ $t('message.I_DONT_RECOMMEND') }}
        </p>
        <EditableLabel
          v-model="ratingComment"
          class="rating__wrapper__comment"
          :placeholder="`${$t('message.ADD_A_COMMENT')} ${$t('message.CONTENT_BETWEEN_PARENTHESES', {
            content: $t('message.OPTIONAL').toLowerCase()
          })}`"
          use-textarea
        />
        <p class="rating__message">{{ ratingMessage }}</p>
        <p class="rating__message__rated">{{ showRatingSuccess ? $t('message.RATING_SUCCESS_THANKS') : '' }}</p>
      </div>
      <div class="rating__buttons">
        <div class="rating__buttons__first-row">
          <button
            class="rating__buttons__close generic-border generic-border--text-btn"
            @click="closeRatingModal"
          >
            {{ $t('message.CLOSE') }}
          </button>
          <button
            class="rating__buttons__send generic-border generic-border--green generic-border--text-btn"
            :class="{ 'generic-border--disabled': isNotRated }"
            :disabled="isNotRated"
            @click="sendRating"
          >
            {{ $t('message.SEND') }}
          </button>
        </div>
        <p
          class="rating__buttons__never-ask option-button"
          @click="dontAskRatingAgain"
        >
          {{ $t('message.RATING_BUTTON_NEVER_AGAIN') }}
        </p>
      </div>
      <p class="rating__subtitle">{{ $t('message.GLOBAL_RATING_SUBTITLE_INTRO') }}</p>
    </div>
  </Modal>
</template>

<script>
/**
 * Rating modal - A modal to rate the functionnalities or Shizen in it's globality
 *
 * @data {String} modalSubtitle: The question that will appear depending of which functionnality we are actually rating
 * @data {Number} modalTypeId: The ID of the corresponding functionnality
 * @data {Boolean} showRatingModal: Choose whether we display the modal or not
 * @data {Number} selected: Number of stars selected in the modal
 * @data {Number} starsHovered: Number of stars actually hovered in the modal
 * @data {Boolean} isRated: Check if the user clicked on a rate
 * @data {Boolean} showRatingSuccess: Display the thanks for rating message or not
 * @data {Boolean} isGlobalRating: Check if it's a global rating or just a functionnality
 * @data {Function} debouncedCheckRatingStatusByLink: Debouncing the check status to avoid multiple calls on fast url changes
 */

import { debounce } from 'lodash';
import toastr from 'toastr';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
// Utils
import { starMedium } from '@/assets/icons';
import { checkRatingInfosFromPage, ratingIds } from '@/utils/rating-utils';
import { keepIAOnRouteChange } from '@/utils/main';
import { ShizenGlobalEvent, globalEventDispatcher } from '@/utils/event-dispatcher/global';

export default {

  components: {
    EditableLabel,
    Icon,
    Modal,
  },

  data() {
    return {
      ratingComment: '',
      starMedium,
      modalSubtitle: '',
      modalTypeId: null,
      showRatingModal: false,
      selected: null,
      starsHovered: null,
      isNotRated: true,
      showRatingSuccess: false,
      isGlobalRating: false,
      debouncedCheckRatingStatusByLink: debounce(this.checkRatingStatusByLink, 500),
      editIndicatorTimeout: null,
    };
  },

  computed: {
    /** Creating the message that will be displayed depending of which star is hovered/selected */
    ratingMessage() {
      if (this.isGlobalRating) {
        return '';
      }
      if (!this.selected) {
        // Remove the messages if the global modal is prompted
        if (this.starsHovered !== null) {
          return this.$t(`message.RATING_STATUS_${this.starsHovered}`);
        }
        return '';
      }
      if (this.selected && this.selected !== 'NONE') {
        return this.$t(`message.RATING_STATUS_${this.selected}`);
      }
      return '';
    },
    /** Triggers if the user choose to never have the rating again */
    zeroStarStatus() {
      if (this.selected !== null && this.selected === 'NONE') {
        return {
          background: '#a0bad8',
        };
      }
      return {};
    },
  },

  watch: {
    $route(to, from) {
      if (
        to.path !== from.path
        && !keepIAOnRouteChange.includes(to.matched[0].path)
        && !this.$store.state.meeting.isMeetingInProgress
      ) {
        // Debounce it to avoid multiple calls on fast path changes.
        this.debouncedCheckRatingStatusByLink(from);
      }
    },
    // currentTeam is watched to verify if the user has logged in
    currentTeam(newVal, oldVal) {
      if (!(oldVal && oldVal.id) && newVal.id) {
        // Check if the global rating needs to be prompted
        this.$nextTick(() => {
          this.$http.get(`/server/ratings/agreement?type=${ratingIds.GLOBAL_ID}`).then(({ data }) => {
            if (typeof data === 'object' && data !== null) {
              this.showRatingModal = data.rate;
              this.isGlobalRating = data.global;
            }
          });
        });
      }
    },
  },

  created() {
    globalEventDispatcher.register(ShizenGlobalEvent.ACTION_CREATED, () => {
      this.checkFunctionnalityRatingModal(ratingIds.ACTION_CREATION_ID);
    });
    globalEventDispatcher.register(ShizenGlobalEvent.PROBLEM_REPORTED, () => {
      this.checkFunctionnalityRatingModal(ratingIds.PROBLEM_CREATION_ID);
    });
    globalEventDispatcher.register(ShizenGlobalEvent.GW_COMPLETED, () => {
      this.checkFunctionnalityRatingModal(ratingIds.GEMBA_WALK_LEAD_ID);
    });
    globalEventDispatcher.register(ShizenGlobalEvent.MEETING_ENDED, () => {
      this.checkFunctionnalityRatingModal(ratingIds.MEETING_LEAD_ID);
    });
    globalEventDispatcher.register(ShizenGlobalEvent.LEAVING_MEETING_SETTINGS_AFTER_CREATION, () => {
      this.checkFunctionnalityRatingModal(ratingIds.MEETING_CREATION_ID);
    });
    globalEventDispatcher.register(ShizenGlobalEvent.INFORMATION_CREATED, () => {
      this.checkFunctionnalityRatingModal(ratingIds.INFORMATION_CLARITY_ID);
    });
    globalEventDispatcher.register(ShizenGlobalEvent.INDICATOR_INPUT_EDITED, () => {
      // Added a setTimeout to avoid modal spamming when changing an indicator value
      clearTimeout(this.editIndicatorTimeout);
      this.editIndicatorTimeout = setTimeout(() => {
        this.checkFunctionnalityRatingModal(ratingIds.INDICATOR_INPUT_ID);
      }, 1000);
    });
  },

  beforeDestroy() {
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.ACTION_CREATED,
      this.checkFunctionnalityRatingModal,
    );
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.PROBLEM_REPORTED,
      this.checkFunctionnalityRatingModal,
    );
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.GW_COMPLETED,
      this.checkFunctionnalityRatingModal,
    );
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.MEETING_ENDED,
      this.checkFunctionnalityRatingModal,
    );
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.LEAVING_MEETING_SETTINGS_AFTER_CREATION,
      this.checkFunctionnalityRatingModal,
    );
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.INFORMATION_CREATED,
      this.checkFunctionnalityRatingModal,
    );
    globalEventDispatcher.unregister(
      ShizenGlobalEvent.INDICATOR_INPUT_EDITED,
      this.checkFunctionnalityRatingModal,
    );
  },

  methods: {
    onStarClicked(key) {
      this.selected = key;
      this.isNotRated = false;
    },
    /** This triggers the modal from specific fonctionnalities. See rating-utils for more informations */
    checkFunctionnalityRatingModal(ratingIndex) {
      this.$http.get(`/server/ratings/agreement?type=${ratingIndex}`).then(({ data }) => {
        if (typeof data === 'object' && data !== null) {
          this.showRatingModal = data.rate;
          this.isGlobalRating = data.global;
          if (this.showRatingModal) {
            this.modalTypeId = ratingIndex;
            this.modalSubtitle = this.$t(`message.RATING_MESSAGE_${ratingIndex}`);
          }
        }
      });
    },
    /** Checking if the user needs to rate this page/functionnality or not */
    checkRatingStatusByLink(from) {
      if (from !== '/action-creation/0') {
        const typeObject = checkRatingInfosFromPage(from);
        if (typeObject) {
          this.$http.get(`/server/ratings/agreement?type=${typeObject.id}`).then(({ data }) => {
            if (typeof data === 'object' && data !== null) {
              this.showRatingModal = data.rate;
              this.isGlobalRating = data.global;
              if (this.showRatingModal) {
                this.modalTypeId = typeObject.id;
                this.modalSubtitle = typeObject.message;
              }
            }
          });
        }
      }
    },
    /** Closes the rating modal, and reset all of the previous informations in it */
    closeRatingModal() {
      this.ratingComment = '';
      this.showRatingModal = false;
      this.starsHovered = null;
      this.selected = null;
      this.showRatingSuccess = false;
      this.modalSubtitle = '';
      this.modalTypeId = null;
      this.isNotRated = true;
    },
    /** Define style of the stars that are selected or not */
    starStatus(key) {
      const idx = this.selected !== null ? this.selected : this.starsHovered;
      if (idx !== null && idx >= key) {
        return {
          fill: '#ffd500',
        };
      }
      return {};
    },
    /** Triggers if the user choose to never have the rating again */
    dontAskRatingAgain() {
      this.$http.post('/server/ratings/stop', { type: this.modalTypeId || ratingIds.GLOBAL_ID }).then(({ data }) => {
        if (data.success) {
          toastr.info(this.$t('message.RATING_NEVER_AGAIN_MESSAGE'));
          this.closeRatingModal();
        }
      });
    },
    /** Sending the actual user rating to the server */
    sendRating() {
      this.$http.post('/server/ratings', {
        type: this.modalTypeId || ratingIds.GLOBAL_ID,
        rate: this.selected && this.selected !== 'NONE' ? (this.selected + 1) : 0,
        comment: this.ratingComment,
      })
        .then((response) => {
          if (response) {
            this.showRatingSuccess = true;
            setTimeout(() => {
              this.closeRatingModal();
            }, 1000);
          }
        }).catch((err) => {
          if (err.response.data.error) {
            // If the user already rated this page/functionnality, just close the modal
            this.closeRatingModal();
          } else {
            // If there's an error, display a toastr
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.rating {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h1.basic-blue-title {
    text-align: center;
  }

  &__subtitle {
    font-style: italic;
    text-align: center;
    margin: 1em 5em;

    &__question {
      margin: 2em 0.5em 0.5em;
      text-align: center;
      font-weight: bold;
      font-size: 1em;
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &__stars {

      &__item {
        cursor: pointer;
        width: 34px;
        height: 34px;
        padding: 0 0.7em;

        &__none, .option-button {
          padding: 0.3em 0.5em;
        }
      }
    }

    &__comment {
      margin: 10px 5%;
      width: 90%;
    }
  }

  &__message {
    height: 1.4rem;
    font-weight: bold;
    font-size: 0.9em;

    &__rated {
      height: 1.4rem;
      color: green;
      font-size: 1em;
    }
  }

  &__buttons {
    margin: 0 1em;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: space-around;

    &__first-row {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;

      @media (max-width: $smartphone-landscape) {
        flex-direction: column;
      }
    }

    &__send, &__close {
      &.generic-border--text-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 13rem;
        margin: 0.5em 1em;
      }

      &.generic-border--disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    &__never-ask {
      margin-top: 1.5em;
    }
  }
}

</style>
